import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdFlag, MdPerson, MdPoll, MdGroup, MdAttachMoney, MdTrendingUp, MdChevronLeft, MdChevronRight, MdFileDownload, MdCalendarToday } from 'react-icons/md';

function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [customPollMetrics, setCustomPollMetrics] = useState(null);
  const [transactionStats, setTransactionStats] = useState(null);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const navigate = useNavigate();

  const now = new Date();
  const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1);
  const [filterYear, setFilterYear] = useState(now.getFullYear());

  // Date range mode: 'month' or 'range'
  const [filterMode, setFilterMode] = useState('month');
  const todayStr = now.toISOString().split('T')[0];
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        const [metricsRes, customPollsRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/metrics`),
          axios.get(`${API_URL}/api/admin/custom-polls/pending-count`).catch(() => ({ data: { pendingCount: 0, flaggedCount: 0, totalReviewNeeded: 0 } }))
        ]);
        setMetrics(metricsRes.data);
        setCustomPollMetrics(customPollsRes.data);
      } catch (error) {
        console.error('Failed to load metrics:', error);
      }
    };
    loadMetrics();
  }, []);

  // Fetch transactions based on active filter mode
  useEffect(() => {
    if (filterMode !== 'month') return;
    const loadTransactions = async () => {
      setTransactionLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/transaction-stats?month=${filterMonth}&year=${filterYear}`);
        setTransactionStats(res.data);
      } catch {
        setTransactionStats({ transactions: [], summary: { totalRevenue: 0, totalTransactions: 0, totalPurchases: 0, totalPollPointTransactions: 0 } });
      } finally {
        setTransactionLoading(false);
      }
    };
    loadTransactions();
  }, [filterMonth, filterYear, filterMode]);

  useEffect(() => {
    if (filterMode !== 'range' || !startDate || !endDate) return;
    const loadRangeTransactions = async () => {
      setTransactionLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/transaction-stats?start_date=${startDate}&end_date=${endDate}`);
        setTransactionStats(res.data);
      } catch {
        setTransactionStats({ transactions: [], summary: { totalRevenue: 0, totalTransactions: 0, totalPurchases: 0, totalPollPointTransactions: 0 } });
      } finally {
        setTransactionLoading(false);
      }
    };
    loadRangeTransactions();
  }, [startDate, endDate, filterMode]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount / 100);

  const monthLabel = new Date(filterYear, filterMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const goToPrevMonth = () => {
    if (filterMonth === 1) { setFilterMonth(12); setFilterYear(y => y - 1); }
    else setFilterMonth(m => m - 1);
  };

  const goToNextMonth = () => {
    if (filterMonth === now.getMonth() + 1 && filterYear === now.getFullYear()) return;
    if (filterMonth === 12) { setFilterMonth(1); setFilterYear(y => y + 1); }
    else setFilterMonth(m => m + 1);
  };

  const isCurrentMonth = filterMonth === now.getMonth() + 1 && filterYear === now.getFullYear();

  const rangeLabel = startDate && endDate
    ? `${new Date(startDate + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(endDate + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : 'Select dates';

  const exportCSV = async () => {
    try {
      let url = `${API_URL}/api/admin/transaction-stats/export`;
      if (filterMode === 'range' && startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
      } else {
        url += `?month=${filterMonth}&year=${filterYear}`;
      }
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      const filename = filterMode === 'range' && startDate && endDate
        ? `transactions_${startDate}_to_${endDate}.csv`
        : `transactions_${new Date(filterYear, filterMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).replace(' ', '_')}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      console.error('Failed to export CSV');
    }
  };

  const switchToMonth = () => {
    setFilterMode('month');
  };

  const switchToRange = () => {
    setFilterMode('range');
    if (!startDate) {
      const firstOfMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}-01`;
      setStartDate(firstOfMonth);
      setEndDate(todayStr);
    }
  };

  const emptyLabel = filterMode === 'month' ? monthLabel : rangeLabel;

  if (!metrics) {
    return <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
    </div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Metrics */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <div className="card-container" data-testid="metric-users">
          <h3 className="text-gray-600 text-sm font-medium mb-2 font-rubik flex items-center gap-1">
            <MdGroup className="text-primary" /> Total Users
          </h3>
          <p className="text-4xl font-bold text-primary font-rubik">{metrics.totalUsers}</p>
        </div>
        <div className="card-container" data-testid="metric-system-polls">
          <h3 className="text-gray-600 text-sm font-medium mb-2 font-rubik flex items-center gap-1">
            <MdPoll className="text-secondary" /> System Polls
          </h3>
          <p className="text-4xl font-bold text-secondary font-rubik">{metrics.totalSystemPolls}</p>
        </div>
        <div className="card-container" data-testid="metric-custom-polls">
          <h3 className="text-gray-600 text-sm font-medium mb-2 font-rubik flex items-center gap-1">
            <MdPerson className="text-tertiary" /> Custom Polls
          </h3>
          <p className="text-4xl font-bold text-tertiary font-rubik">{metrics.totalCustomPolls}</p>
        </div>
        <div className="card-container" data-testid="metric-responses">
          <h3 className="text-gray-600 text-sm font-medium mb-2 font-rubik">Total Responses</h3>
          <p className="text-4xl font-bold text-gray-700 font-rubik">{metrics.totalResponses}</p>
        </div>
      </div>

      {/* Navigation Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
        <button onClick={() => navigate('/admin/polls')} className="card-container hover:shadow-card-hover transition-all text-left" data-testid="manage-polls-button">
          <h3 className="text-2xl font-bold text-gray-800 mb-2 font-rubik">Manage Polls</h3>
          <p className="text-gray-600 font-rubik">Create, edit, and delete poll questions</p>
        </button>
        <button onClick={() => navigate('/admin/tags')} className="card-container hover:shadow-card-hover transition-all text-left" data-testid="manage-tags-button">
          <h3 className="text-2xl font-bold text-gray-800 mb-2 font-rubik">Manage Tags</h3>
          <p className="text-gray-600 font-rubik">Create, edit, and delete tag categories</p>
        </button>
        <button onClick={() => navigate('/admin/flagged-polls')} className="card-container hover:shadow-card-hover transition-all text-left border-2 border-danger" data-testid="manage-flagged-polls-button">
          <h3 className="text-2xl font-bold text-gray-800 mb-2 font-rubik flex items-center gap-2">
            <MdFlag className="text-danger" /> Flagged Polls
          </h3>
          <p className="text-gray-600 font-rubik">Review and manage reported poll questions</p>
          {metrics?.totalFlaggedPolls > 0 && (
            <span className="inline-block mt-2 px-3 py-1 bg-danger text-white text-sm font-medium rounded-full">
              {metrics.totalFlaggedPolls} need review
            </span>
          )}
        </button>
        <button onClick={() => navigate('/admin/custom-polls')} className="card-container hover:shadow-card-hover transition-all text-left border-2 border-secondary" data-testid="manage-custom-polls-button">
          <h3 className="text-2xl font-bold text-gray-800 mb-2 font-rubik flex items-center gap-2">
            <MdPerson className="text-secondary" /> Custom Polls
          </h3>
          <p className="text-gray-600 font-rubik">Review and approve user-created polls</p>
          {customPollMetrics?.totalReviewNeeded > 0 && (
            <span className="inline-block mt-2 px-3 py-1 bg-secondary text-white text-sm font-medium rounded-full">
              {customPollMetrics.totalReviewNeeded} need review
            </span>
          )}
        </button>
        <button onClick={() => navigate('/admin/flagged-reps')} className="card-container hover:shadow-card-hover transition-all text-left border-2 border-orange-400" data-testid="manage-flagged-reps-button">
          <h3 className="text-2xl font-bold text-gray-800 mb-2 font-rubik flex items-center gap-2">
            <MdFlag className="text-orange-500" /> Flagged Reps
          </h3>
          <p className="text-gray-600 font-rubik">Review reported representative profiles</p>
          {metrics?.totalFlaggedReps > 0 && (
            <span className="inline-block mt-2 px-3 py-1 bg-orange-500 text-white text-sm font-medium rounded-full">
              {metrics.totalFlaggedReps} need review
            </span>
          )}
        </button>
      </div>

      {/* Transaction Statistics Card */}
      <div className="card-container mb-8" data-testid="transaction-stats-card">
        {/* Header row */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-xl font-bold text-gray-800 font-rubik flex items-center gap-2">
            <MdAttachMoney className="text-green-500" />
            Transaction Statistics
          </h3>

          <div className="flex items-center gap-3">
            {/* Mode toggle */}
            <div className="flex bg-gray-100 rounded-lg p-0.5 text-xs font-medium" data-testid="filter-mode-toggle">
              <button
                onClick={switchToMonth}
                className={`px-3 py-1.5 rounded-md transition-colors ${filterMode === 'month' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="filter-mode-month"
              >
                Monthly
              </button>
              <button
                onClick={switchToRange}
                className={`px-3 py-1.5 rounded-md transition-colors flex items-center gap-1 ${filterMode === 'range' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="filter-mode-range"
              >
                <MdCalendarToday className="text-xs" /> Custom
              </button>
            </div>

            {/* Monthly nav */}
            {filterMode === 'month' && (
              <div className="flex items-center gap-1" data-testid="transaction-month-filter">
                <button onClick={goToPrevMonth} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-500" data-testid="transaction-prev-month" aria-label="Previous month">
                  <MdChevronLeft className="text-xl" />
                </button>
                <span className="text-sm font-medium text-gray-700 font-rubik min-w-[140px] text-center" data-testid="transaction-month-label">
                  {monthLabel}
                </span>
                <button onClick={goToNextMonth} disabled={isCurrentMonth} className={`p-1.5 rounded-lg transition-colors ${isCurrentMonth ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-500'}`} data-testid="transaction-next-month" aria-label="Next month">
                  <MdChevronRight className="text-xl" />
                </button>
              </div>
            )}

            {/* Date range inputs */}
            {filterMode === 'range' && (
              <div className="flex items-center gap-2" data-testid="transaction-date-range">
                <input
                  type="date"
                  value={startDate}
                  max={endDate || todayStr}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary focus:border-primary"
                  data-testid="transaction-start-date"
                />
                <span className="text-gray-400 text-xs">to</span>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  max={todayStr}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary focus:border-primary"
                  data-testid="transaction-end-date"
                />
              </div>
            )}
          </div>
        </div>

        {/* Summary pills + Export */}
        {transactionStats?.summary && (
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4 text-sm">
              <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                Revenue: {formatCurrency(transactionStats.summary.totalRevenue || 0)}
              </div>
              <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                {transactionStats.summary.totalTransactions || 0} Transactions
              </div>
            </div>
            <button
              onClick={exportCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              data-testid="transaction-export-csv-btn"
            >
              <MdFileDownload className="text-base" />
              Export CSV
            </button>
          </div>
        )}

        {transactionLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
          </div>
        ) : transactionStats?.transactions && transactionStats.transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="transaction-table">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-gray-600 font-medium font-rubik text-sm">Date</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-medium font-rubik text-sm">User</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-medium font-rubik text-sm">Type</th>
                  <th className="text-right py-3 px-4 text-gray-600 font-medium font-rubik text-sm">
                    <div className="flex items-center justify-end gap-1"><MdTrendingUp className="text-blue-500" /> Points</div>
                  </th>
                  <th className="text-right py-3 px-4 text-gray-600 font-medium font-rubik text-sm">
                    <div className="flex items-center justify-end gap-1"><MdAttachMoney className="text-green-500" /> Amount</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactionStats.transactions.map((tx, idx) => (
                  <tr key={tx.id || idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-3 px-4 text-sm text-gray-700 font-rubik">
                      {tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-700 font-rubik">{tx.username || 'Unknown'}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        tx.type === 'stripe_payment' ? 'bg-green-100 text-green-700' :
                        tx.type === 'points_spent' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {tx.type === 'stripe_payment' ? 'Stripe Payment' : tx.type === 'points_spent' ? 'Points Spent' : (tx.type || 'unknown').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-right font-medium text-blue-600">
                      {tx.poll_points ? `-${tx.poll_points}` : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-right font-medium text-green-600">
                      {tx.amount ? formatCurrency(tx.amount) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-10 text-gray-500">
            <MdAttachMoney className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p className="font-rubik">No transactions for {emptyLabel}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
